import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getPlans, Plan } from '../services/planService';
import seoConfig from './seo.config.json';

type SchemaPageType = 'WebPage' | 'CollectionPage' | 'CheckoutPage';
type CatalogPlan = Pick<Plan, 'id' | 'name' | 'description' | 'price' | 'currency'>;
type SeoConfig = { title: string; description: string; url: string; image: string; pageType?: SchemaPageType; includeOfferCatalog?: boolean; staticPlans?: CatalogPlan[] };

const siteUrl = 'https://learnaihub.net';
const organizationId = `${siteUrl}/#organization`;
const websiteId = `${siteUrl}/#website`;
const logoUrl = `${siteUrl}/logo.png`;

const seoByPath = seoConfig as Record<string, SeoConfig>;

const setMetaTag = (selector: string, attribute: 'name' | 'property', value: string, content: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) { element = document.createElement('meta'); element.setAttribute(attribute, value); document.head.appendChild(element); }
  element.setAttribute('content', content);
};

const setCanonical = (href: string) => {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) { element = document.createElement('link'); element.setAttribute('rel', 'canonical'); document.head.appendChild(element); }
  element.setAttribute('href', href);
};

const buildOfferCatalog = (plans: CatalogPlan[]) => ({
  '@type': 'OfferCatalog', '@id': `${siteUrl}/pricing#membership-plans`, name: 'LearnAI Hub Membership Plans',
  description: 'Monthly memberships for access to LearnAI Hub online courses.', numberOfItems: plans.length,
  itemListOrder: 'https://schema.org/ItemListOrderAscending',
  itemListElement: plans.map((plan, index) => ({
    '@type': 'ListItem', position: index + 1,
    item: {
      '@type': 'Service', '@id': `${siteUrl}/pricing#plan-${plan.id}`, name: `${plan.name} Membership`,
      description: plan.description || `Monthly access to LearnAI Hub courses through the ${plan.name} membership.`,
      serviceType: 'Online AI learning membership', provider: { '@id': organizationId },
      offers: { '@type': 'Offer', name: `${plan.name} Monthly Membership`, price: String(plan.price), priceCurrency: plan.currency,
        availability: 'https://schema.org/InStock', seller: { '@id': organizationId },
        priceSpecification: { '@type': 'UnitPriceSpecification', price: String(plan.price), priceCurrency: plan.currency, billingDuration: 'P1M', billingIncrement: 1, unitText: 'MONTH' } },
    },
  })),
});

const buildSchema = (seo: SeoConfig, plans: Plan[] = []) => {
  const organization = { '@type': 'Organization', '@id': organizationId, name: 'LearnAI Hub', url: `${siteUrl}/`, logo: logoUrl,
    contactPoint: { '@type': 'ContactPoint', email: 'support@learnaihub.net', contactType: 'customer support' } };
  const page = {
    '@type': seo.pageType || 'WebPage', '@id': `${seo.url}#webpage`, url: seo.url, name: seo.title, description: seo.description,
    inLanguage: 'en', isPartOf: { '@id': websiteId }, about: { '@id': organizationId }, publisher: { '@id': organizationId },
    ...(seo.includeOfferCatalog && (plans.length || seo.staticPlans?.length) ? { mainEntity: buildOfferCatalog(plans.length ? plans : seo.staticPlans!) } : {}),
  };
  if (seo.url !== `${siteUrl}/`) return { '@context': 'https://schema.org', ...page };
  return {
    '@context': 'https://schema.org',
    '@graph': [organization, { '@type': 'WebSite', '@id': websiteId, url: `${siteUrl}/`, name: 'LearnAI Hub', publisher: { '@id': organizationId } }, page,
      { '@type': 'Service', '@id': `${siteUrl}/#ai-learning-service`, name: 'LearnAI Hub Online AI Learning Service', serviceType: 'Online AI education and training',
        description: 'Practical online courses covering AI skills, automation, productivity, marketing, and business growth.', provider: { '@id': organizationId }, url: `${siteUrl}/` }],
  };
};

const setSchema = (seo: SeoConfig, plans?: Plan[]) => {
  let element = document.getElementById('route-schema') as HTMLScriptElement | null;
  if (!element) { element = document.createElement('script'); element.id = 'route-schema'; element.type = 'application/ld+json'; document.head.appendChild(element); }
  element.textContent = JSON.stringify(buildSchema(seo, plans));
};

const Seo = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const seo = seoByPath[pathname] || seoByPath['/'];
    let isCurrentRoute = true;
    document.title = seo.title;
    setMetaTag('meta[name="description"]', 'name', 'description', seo.description);
    setMetaTag('meta[property="og:title"]', 'property', 'og:title', seo.title);
    setMetaTag('meta[property="og:description"]', 'property', 'og:description', seo.description);
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', seo.url);
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', 'website');
    setMetaTag('meta[property="og:image"]', 'property', 'og:image', seo.image);
    setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', seo.title);
    setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', seo.description);
    setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', seo.image);
    setCanonical(seo.url);
    setSchema(seo);
    if (seo.includeOfferCatalog) getPlans().then((response) => response.data.filter((plan) => plan.is_active)).then((plans) => { if (isCurrentRoute) setSchema(seo, plans); }).catch(() => undefined);
    return () => { isCurrentRoute = false; };
  }, [pathname]);
  return null;
};

export default Seo;
